/**
 * Mouse look and zoom.
 *
 * The trainer is keyboard-only by design — every command you are asked for is
 * a DCS binding, and adding a clickable cockpit would quietly teach a way of
 * operating the aeroplane that does not exist in the simulator you are
 * training for. But being able to turn your head is not a command; it is how
 * you find the ship in the break and how you keep the deck in sight through
 * the turn. So the mouse does exactly one thing: it moves your eyes.
 */
export class MouseLook {
  /**
   * @param {HTMLCanvasElement} canvas
   * @param {import('../scene/CameraRig.js').CameraRig} rig
   */
  constructor(canvas, rig) {
    this.canvas = canvas
    this.rig = rig
    this.dragging = false
    this.last = { x: 0, y: 0 }
    // Live touches, by pointer id. A phone has no scroll wheel, so the only
    // way to zoom is to notice when there are two fingers down and watch the
    // distance between them.
    this.touches = new Map()
    this.pinchFrom = 0

    this._onDown = (e) => {
      this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY })
      if (this.touches.size === 2) {
        // A second finger turns the gesture into a pinch. Drag stops, or the
        // view lurches sideways every time someone zooms.
        this.dragging = false
        this.pinchFrom = this._spread()
        return
      }
      this.dragging = true
      this.last.x = e.clientX
      this.last.y = e.clientY
      canvas.setPointerCapture?.(e.pointerId)
      canvas.style.cursor = 'grabbing'
    }

    this._onMove = (e) => {
      if (this.touches.has(e.pointerId)) {
        this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY })
      }

      if (this.touches.size === 2) {
        const now = this._spread()
        if (this.pinchFrom > 0 && now > 0) {
          // Fed through the same zoom the wheel uses, so a view's limits and
          // its choice of range-versus-lens are honoured without this having to
          // know which kind of view it is looking at. The sign matches a wheel:
          // fingers apart is zooming in.
          this.rig.applyZoom((this.pinchFrom - now) * 2.6)
        }
        this.pinchFrom = now
        return
      }

      if (!this.dragging) return
      this.rig.applyMouseDelta(e.clientX - this.last.x, e.clientY - this.last.y)
      this.last.x = e.clientX
      this.last.y = e.clientY
    }

    this._onUp = (e) => {
      this.touches.delete(e.pointerId)
      // Lifting one finger of a pinch must not immediately drag the view from
      // wherever the remaining finger happens to be — it is still mid-gesture,
      // and the jump would be the width of the pinch.
      if (this.touches.size === 1) {
        const [only] = this.touches.values()
        this.last.x = only.x
        this.last.y = only.y
        this.dragging = true
      } else {
        this.dragging = false
      }
      this.pinchFrom = 0
      canvas.releasePointerCapture?.(e.pointerId)
      canvas.style.cursor = 'default'
    }

    // pointercancel, not just pointerup. On a touch device the browser can
    // take the gesture away mid-drag and never sends an up event, which leaves
    // `dragging` true — and from then on every finger that touches the screen
    // spins the view with nothing pressed.
    this._onCancel = (e) => {
      this.touches.delete(e.pointerId)
      this.dragging = false
      this.pinchFrom = 0
      canvas.releasePointerCapture?.(e.pointerId)
      canvas.style.cursor = 'default'
    }

    this._onLeave = () => {
      this.dragging = false
      canvas.style.cursor = 'default'
    }

    /** Distance between the two live touches, pixels. */
    this._spread = () => {
      const [a, b] = [...this.touches.values()]
      return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0
    }

    this._onContextMenu = (e) => e.preventDefault()

    this._onWheel = (e) => {
      this.rig.applyZoom(e.deltaY)
      e.preventDefault()
    }

    canvas.addEventListener('pointerdown', this._onDown)
    canvas.addEventListener('pointermove', this._onMove)
    canvas.addEventListener('pointerup', this._onUp)
    canvas.addEventListener('pointercancel', this._onCancel)
    canvas.addEventListener('pointerleave', this._onLeave)
    canvas.addEventListener('wheel', this._onWheel, { passive: false })
    canvas.addEventListener('contextmenu', this._onContextMenu)
  }

  dispose() {
    const c = this.canvas
    c.removeEventListener('pointerdown', this._onDown)
    c.removeEventListener('pointermove', this._onMove)
    c.removeEventListener('pointerup', this._onUp)
    c.removeEventListener('pointercancel', this._onCancel)
    c.removeEventListener('pointerleave', this._onLeave)
    c.removeEventListener('wheel', this._onWheel)
    // Was an inline arrow, which meant dispose() could never remove it and was
    // quietly lying about what it cleaned up.
    c.removeEventListener('contextmenu', this._onContextMenu)
  }
}
